import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { breadcrumb } from '../../admin/layout/header/header.component';
import { DataService } from '../../services/data.service';
import { MessageService, ConfirmationService, ConfirmEventType } from 'primeng/api';
import { PermissionHandlerService } from '../../services/permission-handler.service';
import { TranslatorPipe } from '../../services/translator.pipe';

interface Customer {
  name: string;
  email: string;
  phone: string;
  organization: string;
  userId: string;
}

interface ProductServiceInfo {
  productId: string;
  productName: string;
  productType?: string;
  unitPrice: number | string; // Allow both number and string (e.g., "not decided yet")
  subtotal: number | string;
  vatAmount?: number | string;
  totalAmount: number | string;
  currency?: string;
}

interface OrderHistory {
  status: string;
  timestamp?: Date;
  updatedBy?: string;
  notes?: string;
}

interface Attachment {
  fileName: string;
  filePath: string;
  fileType: string;
  uploadedAt?: Date;
}

interface Order {
  _id?: string;
  orderId: string;
  customer: Customer;
  productService: ProductServiceInfo;
  orderStatus: 'PENDING' | 'ACCEPTED' | 'UPDATE' | 'REJECTED' | 'CANCELED';
  orderNotes?: string;
  attachments?: Attachment[];
  orderHistory?: OrderHistory[];
  assignedTo?: string;
  isActive?: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

@Component({
  selector: 'app-order-detail',
  templateUrl: './order-detail.component.html',
  styleUrls: ['./order-detail.component.scss']
})
export class OrderDetailComponent implements OnInit {
  breadcrumbs: breadcrumb[] = [
    {
      name: this.translate('Orders'),
      link: '/orders',
      visible: true
    },
    {
      name: this.translate('Order Details'),
      link: '',
      visible: false
    }
  ];

  order: Order | null = null;
  spinner: boolean = false;
  orderId: string = '';
  
  // Dialog state
  showStatusUpdateDialog: boolean = false;
  showCommentDialog: boolean = false;
  showAttachmentDialog: boolean = false;
  statusUpdateNotes: string = '';
  newStatus: 'PENDING' | 'ACCEPTED' | 'UPDATE' | 'REJECTED' | 'CANCELED' = 'PENDING';
  newComment: string = '';
  newAttachment: string = '';

  constructor(
    private dataService: DataService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private router: Router,
    public permissionService: PermissionHandlerService
  ) { }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.orderId = params['id'];
      if (this.orderId) {
        this.loadOrderDetails();
      }
    });
  }

  async loadOrderDetails() {
    this.spinner = true;
    
    try {
      const response = await this.dataService.getData(`orders/${this.orderId}`);
      
      if (response.error) {
        console.error('Error loading order details:', response.msg);
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: response.msg || this.translate('Failed to load order details')
        });
        this.router.navigate(['/orders']);
      } else {
        this.order = this.transformOrderData(response.data);
        this.breadcrumbs[1].name = `Order ${this.order.orderId}`;
        this.breadcrumbs[1].visible = true;
      }
      
    } catch (error) {
      console.error('Error loading order details:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to load order details')
      });
      this.router.navigate(['/orders']);
    } finally {
      this.spinner = false;
    }
  }

  transformOrderData(apiData: any): Order {
    return {
      _id: apiData._id,
      orderId: apiData.orderId,
      customer: apiData.customer || {
        name: '',
        email: '',
        phone: '',
        organization: '',
        userId: ''
      },
      productService: apiData.productService || {
        productId: '',
        productName: '',
        unitPrice: 0, // Can be number or string
        subtotal: 0, // Can be number or string
        totalAmount: 0, // Can be number or string
        currency: 'SAR'
      },
      orderStatus: apiData.orderStatus || 'PENDING',
      orderNotes: apiData.orderNotes,
      attachments: apiData.attachments || [],
      orderHistory: apiData.orderHistory || [],
      assignedTo: apiData.assignedTo,
      isActive: apiData.isActive,
      createdBy: apiData.createdBy,
      updatedBy: apiData.updatedBy,
      createdAt: apiData.createdAt ? new Date(apiData.createdAt) : undefined,
      updatedAt: apiData.updatedAt ? new Date(apiData.updatedAt) : undefined
    };
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'ACCEPTED': return 'success';
      case 'UPDATE': return 'warning';
      case 'REJECTED': return 'danger';
      case 'CANCELED': return 'secondary';
      default: return 'info';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'PENDING': return 'Pending';
      case 'ACCEPTED': return 'Accepted';
      case 'UPDATE': return 'Update Required';
      case 'REJECTED': return 'Rejected';
      case 'CANCELED': return 'Canceled';
      default: return status;
    }
  }

  openStatusUpdateDialog() {
    if (!this.order) return;
    this.newStatus = this.order.orderStatus;
    this.statusUpdateNotes = '';
    this.showStatusUpdateDialog = true;
  }

  async updateOrderStatus() {
    if (!this.order) return;

    try {
      const response = await this.dataService.postData(
        `orders/status/${this.order._id}`,
        {
          orderStatus: this.newStatus,
          notes: this.statusUpdateNotes
        }
      );

      if (response.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: response.msg || this.translate('Failed to update order status')
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate('Order status updated successfully')
        });
        this.showStatusUpdateDialog = false;
        this.loadOrderDetails();
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to update order status')
      });
    }
  }

  openCommentDialog() {
    this.newComment = '';
    this.showCommentDialog = true;
  }

  async addComment() {
    if (!this.order || !this.newComment.trim()) return;

    try {
      const response = await this.dataService.postData(
        `orders/comment/${this.order._id}`,
        { comments: this.newComment }
      );

      if (response.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: response.msg || this.translate('Failed to add comment')
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate('Comment added successfully')
        });
        this.showCommentDialog = false;
        this.loadOrderDetails();
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to add comment')
      });
    }
  }

  openAttachmentDialog() {
    this.newAttachment = '';
    this.showAttachmentDialog = true;
  }

  async addAttachment() {
    if (!this.order || !this.newAttachment.trim()) return;

    try {
      const response = await this.dataService.postData(
        `orders/history-attachment/${this.order._id}`,
        { attachment: this.newAttachment }
      );

      if (response.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: response.msg || this.translate('Failed to add attachment')
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate('Attachment added successfully')
        });
        this.showAttachmentDialog = false;
        this.loadOrderDetails();
      }
    } catch (error) {
      console.error('Error adding attachment:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to add attachment')
      });
    }
  }

  async deleteOrder() {
    if (!this.order) return;

    this.confirmationService.confirm({
      message: `${this.translate('Are you sure you want to delete order')} ${this.order.orderId}?`,
      header: this.translate('Confirm Delete'),
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const response = await this.dataService.postData(`orders/${this.order!._id}`, {}, 'DELETE');
          
          if (response.error) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate('Error'),
              detail: response.msg || this.translate('Failed to delete order')
            });
          } else {
            this.messageService.add({
              severity: 'success',
              summary: this.translate('Success'),
              detail: this.translate('Order deleted successfully')
            });
            this.router.navigate(['/orders']);
          }
        } catch (error) {
          console.error('Error deleting order:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate('Error'),
            detail: this.translate('Failed to delete order')
          });
        }
      }
    });
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Check if a price value is numeric
   * @param value The price value (number or string)
   * @returns true if the value is a valid number
   */
  isNumericPrice(value: number | string | undefined): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'number') return true;
    if (typeof value === 'string') {
      // Check if string is a valid number (including decimals)
      const numValue = parseFloat(value);
      return !isNaN(numValue) && isFinite(numValue) && value.trim() !== '';
    }
    return false;
  }

  formatCurrency(amount: number | string, currency: string = 'SAR'): string {
    // If amount is not numeric, return it as-is (text)
    if (!this.isNumericPrice(amount)) {
      return String(amount);
    }
    
    // Format numeric amount
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    
    const currencyMap: { [key: string]: string } = {
      'ر.س': 'SAR',
      'ر.س.': 'SAR',
      'SAR': 'SAR'
    };
    
    const isoCurrency = currencyMap[currency] || currency;
    
    if (isoCurrency.length === 3 && /^[A-Z]{3}$/.test(isoCurrency)) {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: isoCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
      
      if (isoCurrency === 'SAR') {
        return formatted.replace(/SAR|ر\.س\.?|\$|€|£|ر\.س/g, '').trim();
      }
      return formatted;
    } else {
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
      return `${formatted} ${currency}`;
    }
  }

  downloadAttachment(attachment: Attachment) {
    // Implement file download logic
    window.open(attachment.filePath, '_blank');
  }

  goBack() {
    this.router.navigate(['/orders']);
  }

  closeStatusUpdateDialog() {
    this.showStatusUpdateDialog = false;
    this.statusUpdateNotes = '';
  }

  closeCommentDialog() {
    this.showCommentDialog = false;
    this.newComment = '';
  }

  closeAttachmentDialog() {
    this.showAttachmentDialog = false;
    this.newAttachment = '';
  }

  async copyEmailToClipboard(email: string) {
    if (!email) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('Warning'),
        detail: this.translate('No email address to copy')
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(email);
      this.messageService.add({
        severity: 'success',
        summary: this.translate('Success'),
        detail: this.translate('Email copied to clipboard')
      });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = email;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          this.messageService.add({
            severity: 'success',
            summary: this.translate('Success'),
            detail: this.translate('Email copied to clipboard')
          });
        } else {
          throw new Error('Copy command failed');
        }
      } catch (fallbackErr) {
        document.body.removeChild(textArea);
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Failed to copy email to clipboard')
        });
      }
    }
  }

  translate(key: string): string {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }
}
